import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, ArrowRight, Clock, Coffee } from 'lucide-react';

const ChemistryMeetingSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="chemistry"
      ref={ref}
      className="py-24 md:py-32 bg-neutral-50"
      data-testid="chemistry-section"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
            >
              {language === 'fr' ? 'Premier contact' : 'First contact'}
            </motion.span>
            
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-4 text-black"
              data-testid="chemistry-title"
            >
              {language === 'fr' ? 'Travaillons ensemble' : 'Let\'s work together'}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="text-xl font-serif text-neutral-700 mb-6"
            >
              {language === 'fr'
                ? 'Le premier échange, c\'est gratuit.'
                : 'The first meeting is free.'}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="text-neutral-600 text-lg leading-relaxed mb-8"
            >
              {language === 'fr'
                ? 'Pour mener à bien votre stratégie digitale, nous devons être en parfaite adéquation. Nous croyons qu\'une collaboration réussie repose sur l\'alchimie entre les équipes. C\'est pourquoi nous proposons un premier échange de 45 minutes pour découvrir vos ambitions et voir si nous sommes faits pour travailler ensemble.'
                : 'To successfully carry out your digital strategy, we need to be perfectly aligned. We believe that a successful collaboration is built on chemistry between teams. That\'s why we offer an initial 45-minute meeting to discover your ambitions and see if we\'re meant to work together.'}
            </motion.p>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap gap-6 mb-10"
            >
              <div className="flex items-center gap-2 text-neutral-600">
                <Clock className="w-5 h-5 text-black" />
                <span className="font-medium">45 min</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-600">
                <Coffee className="w-5 h-5 text-black" />
                <span className="font-medium">{language === 'fr' ? 'Sans engagement' : 'No commitment'}</span>
              </div>
              <div className="flex items-center gap-2 text-neutral-600">
                <Calendar className="w-5 h-5 text-black" />
                <span className="font-medium">{language === 'fr' ? 'Visio ou présentiel' : 'Video or in-person'}</span>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.a
              href="#contact"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 btn-pill bg-black text-white font-semibold px-10 py-5 text-lg"
              data-testid="chemistry-cta"
            >
              {language === 'fr' ? 'Réserver un échange' : 'Book a meeting'}
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.a>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <motion.img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80"
                alt="Meeting"
                className="w-full h-[400px] md:h-[500px] object-cover"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.5 }}
              />
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-6 -left-6 md:-left-10 bg-white rounded-xl shadow-xl p-5 md:p-6 max-w-[240px]"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <span className="font-serif text-xl font-bold text-black">100%</span>
              </div>
              <p className="text-sm text-neutral-600">
                {language === 'fr'
                  ? 'Des clients satisfaits de notre premier échange'
                  : 'Satisfied clients from our first meeting'}
              </p>
            </motion.div>

            {/* Decorative element */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -top-4 -right-4 w-20 h-20 bg-black rounded-xl flex items-center justify-center shadow-xl"
            >
              <Calendar className="w-8 h-8 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ChemistryMeetingSection;
